import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Search, Loader2, CheckCircle, Clock, Zap, AlertTriangle, XCircle, FileText } from "lucide-react";

const ESTADO_CONFIG = {
  recibida: {
    label: "Recibida",
    desc: "Tu petición ha llegado correctamente a nuestro equipo.",
    icon: CheckCircle,
    color: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
    dot: "bg-blue-500",
  },
  borrador: {
    label: "En revisión",
    desc: "El equipo está revisando los detalles de tu petición.",
    icon: Clock,
    color: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-200",
    dot: "bg-amber-500",
  },
  evaluando: {
    label: "Evaluando",
    desc: "Nuestro sistema está generando una evaluación técnica y económica.",
    icon: Zap,
    color: "text-purple-600",
    bg: "bg-purple-50",
    border: "border-purple-200",
    dot: "bg-purple-500",
  },
  evaluado: {
    label: "Evaluada",
    desc: "La evaluación técnica y económica está lista. El equipo se pondrá en contacto contigo próximamente.",
    icon: CheckCircle,
    color: "text-[#007934]",
    bg: "bg-[#f0fdf4]",
    border: "border-[#86efac]",
    dot: "bg-[#007934]",
  },
  en_proceso: {
    label: "En proceso",
    desc: "Tu petición está siendo gestionada por el equipo técnico.",
    icon: Zap,
    color: "text-[#007934]",
    bg: "bg-[#f0fdf4]",
    border: "border-[#86efac]",
    dot: "bg-[#007934]",
  },
  cerrada: {
    label: "Cerrada",
    desc: "La petición ha sido cerrada.",
    icon: XCircle,
    color: "text-gray-500",
    bg: "bg-gray-50",
    border: "border-gray-200",
    dot: "bg-gray-400",
  },
};

const SEMAFORO_MAP = {
  verde: { label: "Viabilidad Alta", color: "text-[#007934]", bg: "bg-[#f0fdf4]", dot: "bg-[#007934]" },
  amarillo: { label: "Viabilidad Media", color: "text-amber-600", bg: "bg-amber-50", dot: "bg-amber-500" },
  rojo: { label: "Requiere análisis adicional", color: "text-red-600", bg: "bg-red-50", dot: "bg-red-500" },
};

const STEPS = [
  { key: "recibida", label: "Recibida" },
  { key: "en_revision", label: "En revisión" },
  { key: "evaluando", label: "Evaluando" },
  { key: "evaluado", label: "Evaluada" },
];

function getStepIndex(estado) {
  if (estado === "recibida") return 0;
  if (estado === "borrador" || estado === "en_revision") return 1;
  if (estado === "evaluando") return 2;
  if (estado === "evaluado" || estado === "en_proceso") return 3;
  if (estado === "cerrada") return 4;
  return 0;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function SeguimientoItPeticion() {
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [peticion, setPeticion] = useState(null);
  const [error, setError] = useState("");

  // Auto-fill reference from URL query param (?ref=PIT-2026-001)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refParam = params.get("ref");
    if (refParam) {
      setRef(refParam.toUpperCase());
    }
  }, []);

  const handleSearch = async (e) => {
    e?.preventDefault();
    const refClean = ref.trim().toUpperCase();
    if (!refClean) return;
    setLoading(true);
    setError("");
    setPeticion(null);
    try {
      const res = await axios.get(`${API}/it-peticiones/public/seguimiento/${refClean}`);
      setPeticion(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("No se encontró ninguna petición con esa referencia. Verifica el número e inténtalo de nuevo.");
      } else {
        setError("Error al consultar. Por favor inténtalo de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  const estadoCfg = peticion ? (ESTADO_CONFIG[peticion.estado] || ESTADO_CONFIG.recibida) : null;
  const stepIdx = peticion ? getStepIndex(peticion.estado) : -1;
  const semaforo = peticion?.semaforo ? SEMAFORO_MAP[peticion.semaforo] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0fdf4] to-[#e8f5e9]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-8 h-8 bg-[#007934] rounded-lg flex items-center justify-center">
            <span className="text-white text-xs font-black">V</span>
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900 leading-tight">Valoriza</p>
            <p className="text-[11px] text-gray-400 leading-tight">Plataformas Corporativas</p>
          </div>
          <span className="ml-auto text-xs text-gray-400 hidden sm:block">Seguimiento de Petición IT</span>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-10">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-4">
            <FileText size={26} className="text-[#007934]" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Seguimiento de Petición IT</h1>
          <p className="text-gray-500 text-sm mt-1">Introduce tu número de referencia para consultar el estado de tu petición</p>
        </div>

        {/* Search form */}
        <form onSubmit={handleSearch} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <label className="block text-xs font-semibold text-gray-500 mb-2">Número de referencia</label>
          <div className="flex gap-3">
            <input
              value={ref}
              onChange={e => setRef(e.target.value)}
              placeholder="PIT-2026-001"
              className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-mono tracking-widest focus:outline-none focus:border-[#007934] uppercase"
              data-testid="input-referencia"
            />
            <button
              type="submit"
              disabled={loading || !ref.trim()}
              className="bg-[#007934] text-white rounded-xl px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-[#006228] transition-colors disabled:opacity-50"
              data-testid="btn-buscar"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              Consultar
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">Formato: PIT-AAAA-NNN (ej. PIT-2026-001)</p>
          {error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 flex items-start gap-2" data-testid="error-msg">
              <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}
        </form>

        {/* Result */}
        {peticion && (
          <div className="space-y-4" data-testid="resultado-peticion">
            {/* Status card */}
            <div className={`bg-white rounded-2xl shadow-sm border p-6 ${estadoCfg.border}`}>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${estadoCfg.bg}`}>
                  <estadoCfg.icon size={22} className={estadoCfg.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${estadoCfg.bg} ${estadoCfg.color}`} data-testid="estado-badge">
                      {estadoCfg.label}
                    </span>
                    <span className="text-xs text-gray-400 font-mono">{peticion.referencia}</span>
                  </div>
                  <h2 className="font-bold text-gray-900 text-base truncate">{peticion.titulo}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{peticion.municipio}{peticion.tipo_contrato ? ` · ${peticion.tipo_contrato}` : ""}</p>
                  <p className="text-xs text-gray-400 mt-1">{estadoCfg.desc}</p>
                </div>
              </div>

              {/* Progress steps */}
              <div className="mt-5 pt-5 border-t border-gray-50">
                <div className="flex items-center">
                  {STEPS.map((step, i) => (
                    <React.Fragment key={step.key}>
                      <div className="flex flex-col items-center gap-1">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                          i < stepIdx ? "bg-[#007934] text-white" :
                          i === stepIdx ? "bg-[#007934] text-white ring-4 ring-[#86efac]" :
                          "bg-gray-100 text-gray-400"
                        }`}>
                          {i < stepIdx ? "✓" : i + 1}
                        </div>
                        <span className={`text-[10px] font-medium text-center leading-tight ${i <= stepIdx ? "text-[#007934]" : "text-gray-400"}`}>
                          {step.label}
                        </span>
                      </div>
                      {i < STEPS.length - 1 && (
                        <div className={`flex-1 h-0.5 mb-3 mx-1 ${i < stepIdx ? "bg-[#007934]" : "bg-gray-100"}`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Detalles</h3>
              <div className="space-y-2 text-sm">
                {peticion.solicitante_nombre && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Solicitante</span>
                    <span className="font-medium text-gray-700">{peticion.solicitante_nombre}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Recibida el</span>
                  <span className="text-gray-700">{formatDate(peticion.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Última actualización</span>
                  <span className="text-gray-700">{formatDate(peticion.updated_at)}</span>
                </div>
              </div>
            </div>

            {/* Evaluation summary (if available) */}
            {peticion.evaluacion_resumen && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Evaluación técnica</h3>
                {semaforo && (
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold mb-3 ${semaforo.bg} ${semaforo.color}`}>
                    <span className={`w-2 h-2 rounded-full ${semaforo.dot}`} />
                    {semaforo.label}
                  </div>
                )}
                <p className="text-sm text-gray-600 leading-relaxed">{peticion.evaluacion_resumen}</p>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  {peticion.duracion_estimada_meses && (
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-400 mb-1">Duración estimada</p>
                      <p className="text-lg font-bold text-gray-900">{peticion.duracion_estimada_meses} meses</p>
                    </div>
                  )}
                  {peticion.coste_total && (
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-400 mb-1">Coste estimado</p>
                      <p className="text-lg font-bold text-[#007934]">{Number(peticion.coste_total).toLocaleString("es-ES")} €</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Next steps */}
            <div className="bg-[#fffbeb] border border-amber-100 rounded-2xl p-5">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">¿Qué ocurre ahora?</p>
              <p className="text-sm text-amber-700 leading-relaxed">
                {stepIdx <= 1
                  ? "El equipo de Plataformas Corporativas revisará tu petición y se pondrá en contacto contigo para confirmar los detalles y planificar la evaluación técnica."
                  : stepIdx === 2
                  ? "Nuestro sistema de Pando está analizando tu petición. En breve tendrás una estimación técnica y económica completa."
                  : "La evaluación está lista. Nuestro equipo comercial y técnico te contactará para presentarte la propuesta y los próximos pasos."
                }
              </p>
            </div>

            {/* New search */}
            <div className="text-center">
              <button
                onClick={() => { setPeticion(null); setRef(""); setError(""); }}
                className="text-[#007934] text-sm font-medium hover:underline"
                data-testid="btn-nueva-busqueda"
              >
                Consultar otra referencia
              </button>
            </div>
          </div>
        )}

        {/* Contact */}
        <p className="text-center text-xs text-gray-400 mt-8">
          ¿Tienes alguna duda? Contacta con nosotros en{" "}
          <a href="mailto:plataformascorporativas@valorizasm.com" className="hover:text-[#007934] underline">
            plataformascorporativas@valorizasm.com
          </a>
        </p>

        {/* Link to submit a new request */}
        <div className="text-center mt-4">
          <a href="/solicitar-it" className="text-xs text-gray-400 hover:text-[#007934] transition-colors">
            ¿Quieres enviar una nueva petición? → Formulario de solicitud
          </a>
        </div>
      </div>
    </div>
  );
}
